/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, useMemo } from 'react';
import { Await } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import BuyNowPayLater from '@/extensions/bnpl/components/buy-now-pay-later';
import PaypalInstallmentsMessage from '@/extensions/bnpl/components/paypal-installments-message';
import { useBnpl } from '@/extensions/bnpl/context/bnpl-context';
import { useOptionalProductView } from '@/providers/product-view';
import { useCurrentVariant } from '@/hooks/product/use-current-variant';
import { getPriceData } from '@/components/product-price/utils';

const EMPTY_PRODUCT = {} as ShopperProducts.schemas['Product'];

function getSiteCountryCode(localeId: string): string {
    const countryCode = localeId.split('-').at(1);
    return countryCode ? countryCode.toUpperCase() : localeId.toUpperCase();
}

function DefaultBnplMessage() {
    const bnpl = useBnpl();

    if (!bnpl) {
        return null;
    }

    const { messagePromise, learnMorePromise } = bnpl;

    return (
        <Suspense fallback={null}>
            <Await resolve={messagePromise} errorElement={null}>
                {(messageData) => (
                    <Suspense fallback={null}>
                        <Await resolve={learnMorePromise} errorElement={null}>
                            {(learnMoreData) => (
                                <BuyNowPayLater messageData={messageData} learnMoreData={learnMoreData} />
                            )}
                        </Await>
                    </Suspense>
                )}
            </Await>
        </Suspense>
    );
}

export default function BnplTarget() {
    const bnpl = useBnpl();
    const { language } = useSite();
    const productView = useOptionalProductView();
    const product = productView?.product ?? EMPTY_PRODUCT;
    const currentVariant = useCurrentVariant({ product, isChildProduct: !productView });

    const installmentsConfig = bnpl?.installmentsConfig;
    const placement = installmentsConfig?.placement ?? 'product';

    const priceValue = useMemo(() => {
        const priceProduct = currentVariant ?? productView?.product;
        if (!priceProduct) {
            return 0;
        }

        return getPriceData(priceProduct).currentPrice;
    }, [currentVariant, productView?.product]);

    if (!bnpl) {
        return null;
    }

    const paypalUseSDKforInstallments = installmentsConfig?.paypalUseSDKforInstallments ?? false;
    const paypalClientId = installmentsConfig?.paypalClientId ?? '';
    const usePaypalSdk = paypalUseSDKforInstallments && Boolean(paypalClientId);

    if (usePaypalSdk) {
        return (
            <PaypalInstallmentsMessage
                amount={priceValue}
                placement={placement}
                buyerCountry={getSiteCountryCode(language)}
                clientId={paypalClientId}
            />
        );
    }

    return <DefaultBnplMessage />;
}
